/**
 * Portal Kit: validação no navegador, antes de gravar.
 *
 * Espelha as regras do `manifesto.schema.json` e as semânticas do ADR 0024 que
 * o editor consegue conferir sozinho (S03, S09, S11, S12). Não substitui o
 * servidor: ele valida de novo e o 422 dele aparece na tela. Serve para a pessoa
 * ver o erro no campo, na hora, em vez de descobrir depois de clicar em Salvar.
 *
 * Funções puras: recebem o que o formulário tem e devolvem a lista de erros.
 */
import type {
  AtivoTicker,
  ColunaComBase,
  ConfigAgro,
  ConfigFonte,
  ConfigFutebol,
  ConfigHoroscopo,
  ConfigLoterias,
  ConfigMapa,
  ConfigMercado,
  ConfigServicos,
  ConfigTempo,
  Conteudo,
  ErroDeCampo,
  LimiaresAgro,
  Local,
  Marca,
  Rodape,
  SecaoId,
  Seo,
  TextosPagina,
} from '@/types/portal';

// ─── expressões do schema ────────────────────────────────────────────────────

export const RE_SLUG = /^(?=.*[a-z])[a-z0-9]+(-[a-z0-9]+)*$/;
export const RE_HTTPS = /^https:\/\/[^\s/?#@]+([/?#][^\s]*)?$/;
export const RE_COR = /^#[0-9a-fA-F]{6}$/;
export const RE_SEM_MARCACAO = /^[^<>]+$/;
export const RE_SEM_MARCACAO_OU_VAZIO = /^[^<>]*$/;
export const RE_SIGLA = /^[A-Z0-9]{2,12}$/;
export const RE_IDIOMA_OG = /^[a-z]{2}_[A-Z]{2}$/;
export const RE_MODELO_DATELINE = /^(?=.*\{praca\})([^{}<>]|\{praca\}|\{sigla\})+$/;
export const RE_PREFIXO_SLUG = /^[a-z0-9]+(-[a-z0-9]+)*-?$/;
export const RE_PREFIXO_SLUG_POST = /^[a-z0-9]+(-[a-z0-9]+)*-$/;
export const RE_META = /^[A-Za-z_][A-Za-z0-9_-]{0,63}$/;
export const RE_CAMINHO_LITERAL = /^[a-z0-9]+(-[a-z0-9]+)*(\/[a-z0-9]+(-[a-z0-9]+)*)*\/$/;

/** Primeiro segmento que pertence ao WordPress e nunca pode ser base de coluna. */
export const RESERVADOS_WP = [
  'category',
  'tag',
  'author',
  'feed',
  'page',
  'search',
  'comments',
  'embed',
  'trackback',
  'attachment',
  'xmlrpc',
  'sitemap',
  'robots',
  'llms',
  'favicon',
  'index',
  'admin',
  'login',
] as const;

export const RE_BASE =
  /^(?!wp-|wp\/|(?:category|tag|author|feed|page|search|comments|embed|trackback|attachment|xmlrpc|sitemap|robots|llms|favicon|index|admin|login)(?:\/|$))[a-z0-9]+(-[a-z0-9]+)*(\/[a-z0-9]+(-[a-z0-9]+)*)*$/;

// ─── utilitários ─────────────────────────────────────────────────────────────

type Erros = ErroDeCampo[];

function erro(erros: Erros, campo: string, mensagem: string): void {
  erros.push({ campo, mensagem });
}

/** Número dentro de [min, max]; `inteiro` exige valor sem casas decimais. */
export function conferirNumero(
  erros: Erros,
  campo: string,
  valor: unknown,
  min: number,
  max: number,
  rotulo: string,
  inteiro = true,
): void {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    erro(erros, campo, `${rotulo}: informe um número.`);
    return;
  }
  if (inteiro && !Number.isInteger(valor)) {
    erro(erros, campo, `${rotulo}: use um número inteiro.`);
    return;
  }
  if (valor < min || valor > max) {
    erro(erros, campo, `${rotulo}: entre ${min} e ${max}.`);
  }
}

function conferirSlug(erros: Erros, campo: string, valor: string, rotulo: string): void {
  if (!RE_SLUG.test(valor) || valor.length > 80) {
    erro(
      erros,
      campo,
      `${rotulo}: use minúsculas, números e hífen, com ao menos uma letra (por exemplo "esportes").`,
    );
  }
}

function conferirHttps(erros: Erros, campo: string, valor: string, rotulo: string): void {
  if (!RE_HTTPS.test(valor) || valor.length > 2048) {
    erro(erros, campo, `${rotulo}: endereço completo começando com https://.`);
  }
}

function conferirTexto(
  erros: Erros,
  campo: string,
  valor: string,
  rotulo: string,
  max: number,
  obrigatorio = true,
): void {
  if (valor.length === 0) {
    if (obrigatorio) erro(erros, campo, `${rotulo}: preencha.`);
    return;
  }
  if (valor.length > max) erro(erros, campo, `${rotulo}: no máximo ${max} caracteres.`);
  if (!RE_SEM_MARCACAO.test(valor)) erro(erros, campo, `${rotulo}: sem os sinais < ou >.`);
}

function repetidos<T>(itens: T[], chave: (i: T) => string): string[] {
  const vistos = new Set<string>();
  const dup = new Set<string>();
  for (const i of itens) {
    const k = chave(i);
    if (vistos.has(k)) dup.add(k);
    vistos.add(k);
  }
  return [...dup];
}

// ─── endereços (S03) ─────────────────────────────────────────────────────────

export function validarBase(base: string, campo = 'config.base'): Erros {
  const erros: Erros = [];
  const b = base.trim();
  if (b.length === 0) {
    erro(erros, campo, 'Endereço: preencha (por exemplo "previsao-do-tempo").');
    return erros;
  }
  if (b.length > 80) {
    erro(erros, campo, 'Endereço: no máximo 80 caracteres.');
    return erros;
  }
  const primeiro = b.split('/')[0];
  if (primeiro.startsWith('wp-') || (RESERVADOS_WP as readonly string[]).includes(primeiro)) {
    erro(
      erros,
      campo,
      `Endereço: "${primeiro}" é reservado do WordPress. Escolha outro (wp-*, category, tag, author, feed, page, search...).`,
    );
    return erros;
  }
  if (!RE_BASE.test(b)) {
    erro(
      erros,
      campo,
      'Endereço: minúsculas, números e hífen, sem barra no começo nem no fim (por exemplo "previsao-do-tempo").',
    );
  }
  return erros;
}

/** Duas bases iguais, ou uma dentro da outra, brigam pela mesma URL. */
export function validarBasesUnicas(
  bases: Partial<Record<ColunaComBase, string>>,
): Map<ColunaComBase, string[]> {
  const porColuna = new Map<ColunaComBase, string[]>();
  const entradas = Object.entries(bases) as [ColunaComBase, string][];
  for (const [c, a] of entradas) {
    for (const [d, b] of entradas) {
      if (c === d || a === '' || b === '') continue;
      if (a === b || b.startsWith(`${a}/`)) {
        const msg =
          a === b
            ? `Igual ao endereço de "${d}". Cada coluna precisa do seu.`
            : `"${a}" é o começo do endereço de "${d}" (${b}). Uma não pode ficar dentro da outra.`;
        porColuna.set(c, [...(porColuna.get(c) ?? []), msg]);
      }
    }
  }
  return porColuna;
}

// ─── listas de lugares (cidades e praças) ────────────────────────────────────

export function validarLocais(lista: Local[], campo: string, rotulo: string): Erros {
  const erros: Erros = [];
  if (lista.length < 1) erro(erros, campo, `${rotulo}: inclua ao menos um município.`);
  if (lista.length > 200) erro(erros, campo, `${rotulo}: no máximo 200 municípios.`);
  const porIbge = repetidos(lista, (l) => l.ibge);
  if (porIbge.length > 0)
    erro(erros, campo, `${rotulo}: município repetido (IBGE ${porIbge.join(', ')}).`);
  const porSlug = repetidos(lista, (l) => l.slug);
  if (porSlug.length > 0 && porIbge.length === 0) {
    erro(erros, campo, `${rotulo}: dois municípios com o mesmo endereço (${porSlug.join(', ')}).`);
  }
  return erros;
}

// ─── ativos do mercado (S12) ─────────────────────────────────────────────────

export function validarAtivos(
  ticker: AtivoTicker[],
  painel: string[],
  campo = 'config.ticker',
): Erros {
  const erros: Erros = [];
  if (ticker.length > 30) erro(erros, campo, 'Ticker: no máximo 30 ativos.');
  ticker.forEach((a, i) => {
    const onde = `${campo}.${i}`;
    if (a.ativo.trim() === '')
      erro(
        erros,
        `${onde}.ativo`,
        `Ativo ${i + 1}: informe o código (por exemplo IBOVESPA ou USD-BRL).`,
      );
    else conferirTexto(erros, `${onde}.ativo`, a.ativo, `Ativo ${i + 1}`, 40);
    if (a.rotulo.trim() === '')
      erro(erros, `${onde}.rotulo`, `Ativo ${i + 1}: informe o rótulo que aparece na tela.`);
    else conferirTexto(erros, `${onde}.rotulo`, a.rotulo, `Rótulo do ativo ${i + 1}`, 30);
  });
  const rotulos = ticker.map((a) => a.rotulo);
  const dup = repetidos(rotulos, (r) => r).filter((r) => r !== '');
  if (dup.length > 0) erro(erros, campo, `Rótulo repetido no ticker: ${dup.join(', ')}.`);
  const faltando = painel.filter((p) => !rotulos.includes(p));
  if (faltando.length > 0) {
    erro(
      erros,
      'config.painel_materia',
      `O painel das matérias usa rótulo que não está no ticker: ${faltando.join(', ')}.`,
    );
  }
  return erros;
}

// ─── limiares do agro (S11) ──────────────────────────────────────────────────

export function validarLimiares(l: LimiaresAgro, campo = 'config.limiares'): Erros {
  const erros: Erros = [];
  conferirNumero(
    erros,
    `${campo}.geada.risco_ate_c`,
    l.geada.risco_ate_c,
    -10,
    10,
    'Risco de geada (°C)',
    false,
  );
  conferirNumero(
    erros,
    `${campo}.geada.alto_ate_c`,
    l.geada.alto_ate_c,
    -10,
    10,
    'Risco alto de geada (°C)',
    false,
  );
  if (
    Number.isFinite(l.geada.alto_ate_c) &&
    Number.isFinite(l.geada.risco_ate_c) &&
    l.geada.alto_ate_c > l.geada.risco_ate_c
  ) {
    erro(
      erros,
      `${campo}.geada.alto_ate_c`,
      'Risco alto de geada não pode ser maior que o de risco: a temperatura do alto é a mais fria.',
    );
  }
  const p = l.pulverizacao;
  conferirNumero(
    erros,
    `${campo}.pulverizacao.vento_max_kmh`,
    p.vento_max_kmh,
    0,
    60,
    'Vento máximo (km/h)',
    false,
  );
  conferirNumero(
    erros,
    `${campo}.pulverizacao.umidade_min_pct`,
    p.umidade_min_pct,
    0,
    100,
    'Umidade mínima (%)',
    false,
  );
  conferirNumero(
    erros,
    `${campo}.pulverizacao.temp_max_c`,
    p.temp_max_c,
    0,
    50,
    'Temperatura máxima (°C)',
    false,
  );
  conferirNumero(
    erros,
    `${campo}.pulverizacao.chuva_max_mm`,
    p.chuva_max_mm,
    0,
    20,
    'Chuva máxima (mm)',
    false,
  );
  return erros;
}

// ─── blocos de texto ─────────────────────────────────────────────────────────

function validarTextos(t: TextosPagina, campo = 'config.textos'): Erros {
  const erros: Erros = [];
  conferirTexto(erros, `${campo}.indice_titulo`, t.indice_titulo, 'Título do índice', 300);
  conferirTexto(erros, `${campo}.indice_descricao`, t.indice_descricao, 'Descrição do índice', 300);
  conferirTexto(erros, `${campo}.item_titulo`, t.item_titulo, 'Título da página', 300);
  conferirTexto(erros, `${campo}.item_descricao`, t.item_descricao, 'Descrição da página', 300);
  return erros;
}

// ─── colunas ─────────────────────────────────────────────────────────────────

export function validarTempo(c: ConfigTempo): Erros {
  const erros: Erros = [];
  conferirSlug(erros, 'config.categoria', c.categoria, 'Categoria');
  erros.push(...validarLocais(c.cidades, 'config.cidades', 'Cidades'));
  conferirNumero(erros, 'config.dias_previsao', c.dias_previsao, 1, 16, 'Dias de previsão');
  conferirNumero(erros, 'config.cache_s', c.cache_s, 30, 86_400, 'Cache (s)');
  erros.push(...validarTextos(c.textos));
  return erros;
}

export function validarMapa(c: ConfigMapa): Erros {
  const erros: Erros = [];
  conferirNumero(erros, 'config.grau_minimo', c.grau_minimo, 1, 3, 'Grau mínimo do aviso');
  conferirNumero(
    erros,
    'config.cache_avisos_s',
    c.cache_avisos_s,
    30,
    86_400,
    'Cache dos avisos (s)',
  );
  return erros;
}

export function validarAgro(c: ConfigAgro): Erros {
  const erros: Erros = [];
  erros.push(...validarLocais(c.pracas, 'config.pracas', 'Praças'));
  conferirNumero(erros, 'config.dias_previsao', c.dias_previsao, 1, 16, 'Dias de previsão');
  conferirNumero(erros, 'config.dias_passados', c.dias_passados, 0, 92, 'Dias passados');
  conferirNumero(erros, 'config.cache_s', c.cache_s, 30, 86_400, 'Cache (s)');
  erros.push(...validarLimiares(c.limiares));
  erros.push(...validarTextos(c.textos));
  return erros;
}

export function validarMercado(c: ConfigMercado): Erros {
  const erros: Erros = [];
  conferirTexto(erros, 'config.titulo', c.titulo, 'Título', 300);
  conferirTexto(erros, 'config.descricao', c.descricao, 'Descrição', 300);
  erros.push(...validarAtivos(c.ticker, c.painel_materia));
  conferirNumero(
    erros,
    'config.cache.painel_s',
    c.cache.painel_s,
    30,
    86_400,
    'Cache do painel (s)',
  );
  conferirNumero(erros, 'config.cache.ativo_s', c.cache.ativo_s, 30, 86_400, 'Cache do ativo (s)');
  conferirNumero(
    erros,
    'config.cache.indicadores_s',
    c.cache.indicadores_s,
    30,
    86_400,
    'Cache dos indicadores (s)',
  );
  conferirNumero(
    erros,
    'config.atraso_declarado_min',
    c.atraso_declarado_min,
    1,
    1440,
    'Atraso declarado (min)',
  );
  conferirNumero(
    erros,
    'config.idade_maxima_s',
    c.idade_maxima_s,
    60,
    86_400,
    'Idade máxima do painel (s)',
  );
  conferirNumero(erros, 'config.manchetes_limite', c.manchetes_limite, 0, 30, 'Manchetes');
  if (!RE_PREFIXO_SLUG.test(c.prefixo_slug_diaria) || c.prefixo_slug_diaria.length > 60) {
    erro(
      erros,
      'config.prefixo_slug_diaria',
      'Prefixo das matérias diárias: minúsculas, números e hífen.',
    );
  }
  return erros;
}

export function validarHoroscopo(c: ConfigHoroscopo): Erros {
  const erros: Erros = [];
  conferirSlug(erros, 'config.categoria', c.categoria, 'Categoria');
  if (c.signos.length < 1) erro(erros, 'config.signos', 'Signos: deixe ao menos um.');
  if (c.texto.pagina_pai_slug !== null && c.texto.pagina_pai_slug !== '') {
    conferirSlug(erros, 'config.texto.pagina_pai_slug', c.texto.pagina_pai_slug, 'Página-mãe');
  }
  if (
    !RE_PREFIXO_SLUG_POST.test(c.texto.prefixo_slug_post) ||
    c.texto.prefixo_slug_post.length > 40
  ) {
    erro(
      erros,
      'config.texto.prefixo_slug_post',
      'Prefixo do post diário: termina com hífen (por exemplo "horoscopo-do-dia-").',
    );
  }
  conferirNumero(
    erros,
    'config.previa_max_caracteres',
    c.previa_max_caracteres,
    80,
    600,
    'Prévia (caracteres)',
  );
  erros.push(...validarTextos(c.textos));
  return erros;
}

export function validarLoterias(c: ConfigLoterias): Erros {
  const erros: Erros = [];
  conferirSlug(erros, 'config.categoria', c.categoria, 'Categoria');
  if (c.modalidades.length < 1)
    erro(erros, 'config.modalidades', 'Modalidades: inclua ao menos uma.');
  if (c.modalidades.length > 20) erro(erros, 'config.modalidades', 'Modalidades: no máximo 20.');
  c.modalidades.forEach((m, i) => {
    conferirSlug(erros, `config.modalidades.${i}.slug`, m.slug, `Modalidade ${i + 1}`);
    conferirTexto(erros, `config.modalidades.${i}.nome`, m.nome, `Nome da modalidade ${i + 1}`, 40);
  });
  const dup = repetidos(c.modalidades, (m) => m.slug);
  if (dup.length > 0) erro(erros, 'config.modalidades', `Modalidade repetida: ${dup.join(', ')}.`);
  conferirNumero(
    erros,
    'config.outros_resultados_max',
    c.outros_resultados_max,
    1,
    20,
    'Outros resultados',
  );
  return erros;
}

export function validarFutebol(c: ConfigFutebol): Erros {
  const erros: Erros = [];
  conferirSlug(erros, 'config.categoria', c.categoria, 'Categoria');
  if (c.series.length < 1) erro(erros, 'config.series', 'Séries: deixe ao menos uma.');
  conferirNumero(
    erros,
    'config.atraso_alerta_min',
    c.atraso_alerta_min,
    1,
    1440,
    'Alerta de atraso (min)',
  );
  conferirNumero(
    erros,
    'config.cache.tabela_s',
    c.cache.tabela_s,
    30,
    86_400,
    'Cache da tabela (s)',
  );
  conferirNumero(
    erros,
    'config.cache.detalhe_s',
    c.cache.detalhe_s,
    30,
    86_400,
    'Cache do detalhe (s)',
  );
  if (c.imagens.tamanhos_px.length < 1 || c.imagens.tamanhos_px.length > 4) {
    erro(erros, 'config.imagens.tamanhos_px', 'Tamanhos de imagem: de 1 a 4.');
  }
  c.imagens.tamanhos_px.forEach((t, i) =>
    conferirNumero(erros, `config.imagens.tamanhos_px.${i}`, t, 16, 512, `Tamanho ${i + 1} (px)`),
  );
  if (new Set(c.imagens.tamanhos_px).size !== c.imagens.tamanhos_px.length) {
    erro(erros, 'config.imagens.tamanhos_px', 'Tamanhos de imagem repetidos.');
  }
  conferirNumero(erros, 'config.home.linhas', c.home.linhas, 3, 20, 'Linhas do cartão da home');
  if (!c.series.some((s) => s.id === c.home.serie)) {
    erro(
      erros,
      'config.home.serie',
      'A série do cartão da home precisa estar entre as séries com página.',
    );
  }
  return erros;
}

export function validarServicos(c: ConfigServicos): Erros {
  const erros: Erros = [];
  conferirTexto(erros, 'config.titulo_home', c.titulo_home, 'Título da seção', 300);
  if (c.faixa.length > 12) erro(erros, 'config.faixa', 'Faixa: no máximo 12 itens.');
  c.faixa.forEach((f, i) => {
    const onde = `config.faixa.${i}`;
    conferirSlug(erros, `${onde}.chave`, f.chave, `Item ${i + 1} (chave)`);
    if (f.rotulo.trim() === '') erro(erros, `${onde}.rotulo`, `Item ${i + 1}: informe o rótulo.`);
    else conferirTexto(erros, `${onde}.rotulo`, f.rotulo, `Rótulo do item ${i + 1}`, 30);
    if ('caminho' in f.alvo) {
      if (!RE_CAMINHO_LITERAL.test(f.alvo.caminho)) {
        erro(
          erros,
          `${onde}.alvo.caminho`,
          `Item ${i + 1}: caminho com barra no fim, sem barra no começo (por exemplo "previsao-do-tempo/").`,
        );
      }
    } else if ('categoria' in f.alvo) {
      conferirSlug(erros, `${onde}.alvo.categoria`, f.alvo.categoria, `Categoria do item ${i + 1}`);
    } else {
      conferirHttps(erros, `${onde}.alvo.url`, f.alvo.url, `Endereço do item ${i + 1}`);
    }
  });
  const dup = repetidos(c.faixa, (f) => f.chave);
  if (dup.length > 0) erro(erros, 'config.faixa', `Chave repetida na faixa: ${dup.join(', ')}.`);
  return erros;
}

export function validarFonte(c: ConfigFonte): Erros {
  const erros: Erros = [];
  conferirTexto(erros, 'config.rotulo', c.rotulo, 'Rótulo', 30);
  c.categorias_isentas.forEach((s, i) =>
    conferirSlug(erros, `config.categorias_isentas.${i}`, s, `Categoria isenta ${i + 1}`),
  );
  if (c.categorias_isentas.length > 40)
    erro(erros, 'config.categorias_isentas', 'Categorias isentas: no máximo 40.');
  if (new Set(c.categorias_isentas).size !== c.categorias_isentas.length) {
    erro(erros, 'config.categorias_isentas', 'Categoria isenta repetida.');
  }
  c.meta_legadas.forEach((m, i) => {
    if (!RE_META.test(m))
      erro(erros, `config.meta_legadas.${i}`, `Meta antiga ${i + 1}: letras, números, _ e -.`);
  });
  if (c.meta_legadas.length > 8) erro(erros, 'config.meta_legadas', 'Metas antigas: no máximo 8.');
  return erros;
}

// ─── blocos ──────────────────────────────────────────────────────────────────

export function validarMarca(m: Marca): Erros {
  const erros: Erros = [];
  conferirTexto(erros, 'config.nome', m.nome, 'Nome', 80);
  if (!RE_SIGLA.test(m.sigla))
    erro(erros, 'config.sigla', 'Sigla: de 2 a 12 letras maiúsculas ou números.');
  if (m.lema !== null && m.lema.length > 160)
    erro(erros, 'config.lema', 'Lema: no máximo 160 caracteres.');
  if (m.edicao !== null && m.edicao.length > 60)
    erro(erros, 'config.edicao', 'Edição: no máximo 60 caracteres.');
  if (m.logo !== null && m.logo !== '') conferirHttps(erros, 'config.logo', m.logo, 'Logo');
  for (const [nome, valor] of Object.entries(m.cores)) {
    if (!RE_COR.test(valor))
      erro(erros, `config.cores.${nome}`, `Cor ${nome}: use o formato #RRGGBB.`);
  }
  if (m.dateline.praca_padrao.trim() === '' || m.dateline.praca_padrao.length > 60) {
    erro(erros, 'config.dateline.praca_padrao', 'Praça padrão da dateline: de 1 a 60 caracteres.');
  }
  if (!RE_MODELO_DATELINE.test(m.dateline.modelo) || m.dateline.modelo.length > 80) {
    erro(
      erros,
      'config.dateline.modelo',
      'Modelo da dateline: precisa de {praca}; {sigla} é opcional (por exemplo "{praca} ({sigla}) —").',
    );
  }
  if (m.autor_padrao.trim() === '' || m.autor_padrao.length > 80) {
    erro(erros, 'config.autor_padrao', 'Autor padrão: de 1 a 80 caracteres.');
  }
  return erros;
}

export function validarRodape(r: Rodape): Erros {
  const erros: Erros = [];
  if (!RE_SEM_MARCACAO_OU_VAZIO.test(r.hora.rotulo) || r.hora.rotulo.length > 30) {
    erro(erros, 'config.hora.rotulo', 'Rótulo da hora: até 30 caracteres, sem < ou >.');
  }
  if (!RE_SEM_MARCACAO_OU_VAZIO.test(r.atraso.rotulo) || r.atraso.rotulo.length > 30) {
    erro(erros, 'config.atraso.rotulo', 'Rótulo do atraso: até 30 caracteres, sem < ou >.');
  }
  conferirNumero(
    erros,
    'config.atraso.alerta_acima_min',
    r.atraso.alerta_acima_min,
    1,
    1440,
    'Alerta de atraso (min)',
  );
  conferirTexto(erros, 'config.fonte.rotulo', r.fonte.rotulo, 'Rótulo da fonte', 30);
  conferirTexto(erros, 'config.selo.texto', r.selo.texto, 'Texto do selo', 80);
  if (r.selo.url !== null && r.selo.url !== '')
    conferirHttps(erros, 'config.selo.url', r.selo.url, 'Endereço do selo');
  if (
    (r.modo === 'completo' || r.modo === 'os_dois') &&
    !r.hora.exibir &&
    !r.atraso.exibir &&
    !r.fonte.exibir
  ) {
    erro(
      erros,
      'config.modo',
      'Neste modo cada bloco mostra a linha de rodapé: ligue ao menos um entre hora, atraso e fonte.',
    );
  }
  return erros;
}

export function validarSeo(s: Seo): Erros {
  const erros: Erros = [];
  if (!RE_IDIOMA_OG.test(s.idioma_og))
    erro(erros, 'config.idioma_og', 'Idioma do Open Graph: formato pt_BR.');
  if (s.imagem_padrao !== null && s.imagem_padrao !== '') {
    conferirHttps(erros, 'config.imagem_padrao', s.imagem_padrao, 'Imagem padrão');
  }
  for (const [campo, rotulo] of [
    ['politica_editorial_slug', 'Página de política editorial'],
    ['expediente_slug', 'Página de expediente'],
  ] as const) {
    const v = s.organizacao[campo];
    if (v !== null && v !== '') conferirSlug(erros, `config.organizacao.${campo}`, v, rotulo);
  }
  s.organizacao.same_as.forEach((u, i) =>
    conferirHttps(erros, `config.organizacao.same_as.${i}`, u, `Perfil ${i + 1}`),
  );
  if (new Set(s.organizacao.same_as).size !== s.organizacao.same_as.length) {
    erro(erros, 'config.organizacao.same_as', 'Perfil repetido.');
  }
  s.perenes_categorias.forEach((c, i) =>
    conferirSlug(erros, `config.perenes_categorias.${i}`, c, `Categoria perene ${i + 1}`),
  );
  s.pereciveis.categorias.forEach((c, i) => {
    conferirSlug(
      erros,
      `config.pereciveis.categorias.${i}.categoria`,
      c.categoria,
      `Categoria perecível ${i + 1}`,
    );
    conferirNumero(
      erros,
      `config.pereciveis.categorias.${i}.dias`,
      c.dias,
      1,
      365,
      `Dias da categoria ${i + 1}`,
    );
  });
  s.pereciveis.prefixos_slug.forEach((p, i) => {
    if (!RE_PREFIXO_SLUG.test(p.prefixo) || p.prefixo.length > 60) {
      erro(
        erros,
        `config.pereciveis.prefixos_slug.${i}.prefixo`,
        `Prefixo ${i + 1}: minúsculas, números e hífen.`,
      );
    }
    conferirNumero(
      erros,
      `config.pereciveis.prefixos_slug.${i}.dias`,
      p.dias,
      1,
      365,
      `Dias do prefixo ${i + 1}`,
    );
  });
  return erros;
}

export function validarConteudo(c: Conteudo): Erros {
  const erros: Erros = [];
  if (c.categorias_noticia.length < 1)
    erro(erros, 'config.categorias_noticia', 'Categorias de notícia: inclua ao menos uma.');
  if (c.categorias_noticia.length > 40)
    erro(erros, 'config.categorias_noticia', 'Categorias de notícia: no máximo 40.');
  c.categorias_noticia.forEach((s, i) =>
    conferirSlug(erros, `config.categorias_noticia.${i}`, s, `Categoria ${i + 1}`),
  );
  if (new Set(c.categorias_noticia).size !== c.categorias_noticia.length) {
    erro(erros, 'config.categorias_noticia', 'Categoria repetida.');
  }
  const r = c.sem_repeticao;
  conferirNumero(
    erros,
    'config.sem_repeticao.janela_arquivo_h',
    r.janela_arquivo_h,
    1,
    720,
    'Janela (h)',
  );
  conferirNumero(
    erros,
    'config.sem_repeticao.teto_arquivo',
    r.teto_arquivo,
    50,
    1000,
    'Teto do arquivo',
  );
  conferirNumero(
    erros,
    'config.sem_repeticao.termos_mesma_noticia',
    r.termos_mesma_noticia,
    2,
    10,
    'Termos em comum',
  );
  conferirNumero(
    erros,
    'config.sem_repeticao.distancia_foto_max',
    r.distancia_foto_max,
    0,
    16,
    'Distância da foto',
  );
  conferirNumero(erros, 'config.sem_repeticao.fator_busca', r.fator_busca, 1, 10, 'Fator de busca');
  return erros;
}

/** Despacha para o validador da seção. A `config` entra tipada pela chave, mas chega `unknown` do despacho. */
export function validarSecao(chave: SecaoId, config: unknown): Erros {
  switch (chave) {
    case 'tempo':
      return validarTempo(config as ConfigTempo);
    case 'mapa':
      return validarMapa(config as ConfigMapa);
    case 'agro':
      return validarAgro(config as ConfigAgro);
    case 'mercado':
      return validarMercado(config as ConfigMercado);
    case 'horoscopo':
      return validarHoroscopo(config as ConfigHoroscopo);
    case 'loterias':
      return validarLoterias(config as ConfigLoterias);
    case 'futebol':
      return validarFutebol(config as ConfigFutebol);
    case 'servicos':
      return validarServicos(config as ConfigServicos);
    case 'fonte':
      return validarFonte(config as ConfigFonte);
    case 'marca':
      return validarMarca(config as Marca);
    case 'rodape':
      return validarRodape(config as Rodape);
    case 'seo':
      return validarSeo(config as Seo);
    case 'conteudo':
      return validarConteudo(config as Conteudo);
  }
}
